import type { PlanktonCategoryWithSlugs } from "~~/server/plankton";

export interface CategorySlug {
  // "slug" will be replaced in the url
  slug: string;
}

// can be used both on the server and the client
export function createCategoryTree(categories: Array<PlanktonCategoryWithSlugs> | undefined, language: string) {
  if (!categories) {
    console.warn("[Create Category Tree] Could not find categories.");
    categories = [];
  }

  const currentLanguage = language;
  const categoryMap = new Map<number, PlanktonCategoryWithSlugs>();

  for (const category of categories) {
    categoryMap.set(category.id, category);
  }

  function getTranslation(category: PlanktonCategoryWithSlugs | undefined) {
    if (!category?.translations) {
      return undefined;
    }

    return category.translations[currentLanguage];
  };

  return {
    getAll: () => [...categories.values()],

    getCategory: (categoryId: number) => {
      return categoryMap.get(categoryId);
    },

    getName: (categoryId: number) => {
      const category = categoryMap.get(categoryId);
      return getTranslation(category)?.name || category?.internalName;
    },

    getDescription: (categoryId: number) => {
      const category = categoryMap.get(categoryId);
      return getTranslation(category)?.description || category?.internalDescription;
    },

    getTranslation: (categoryId: number) => {
      const category = categoryMap.get(categoryId);
      return getTranslation(category);
    },

    getParent: (categoryId: number) => {
      const category = categoryMap.get(categoryId);

      if (!category) {
        return undefined;
      }

      return categoryMap.get(category.parentId);
    },

    getChildren: (categoryId: number) => {
      return categories.filter(category => category.parentId === categoryId);
    },

    getFirstLevel: () => {
      // root is level 1, so we return all categories with level 2
      return categories.filter(category => category.level === 2);
    },

    getCount: () => {
      return categories.length;
    },

    getPath: (categoryId: number) => {
      const category = categoryMap.get(categoryId);

      if (!category) {
        return undefined;
      }

      return category.slugs[currentLanguage]?.slugPath;
    },

    getPaths: (categoryId: number) => {
      const category = categoryMap.get(categoryId);

      if (!category) {
        return undefined;
      }

      const urls: { [key: string]: CategorySlug } = {};

      for (const [language] of Object.entries(category.slugs)) {
        urls[language] = {
          slug: category.slugs[language]?.slugPath ?? "/",
        } satisfies CategorySlug;
      }

      return urls;
    },

    getByPath: (slugPath: string) => {
      return categories.find(category => category.slugs[currentLanguage]?.slugPath === slugPath);
    },

    // untested
    // // getDescendants:  (categoryId: number) => {
    // //   const descendants = new Set<PlanktonCategoryWithSlugs>();
    // //   const stack = [categoryId];
    // //   while (stack.length > 0) {
    // //     const currentCategoryId = stack.pop();
    // //     if (currentCategoryId === undefined) {
    // //       continue;
    // //     }

    // //     const children = categories.filter((category) => category.parentId === currentCategoryId);
    // //     for (const child of children) {
    // //       descendants.add(child);
    // //       stack.push(child.id);
    // //     }
    // //   }

    // //   return Array.from(descendants);
    // // },

  };
}

export type CategoryTree = ReturnType<typeof createCategoryTree>;
